import { updateProfile } from 'firebase/auth'
import { doc, updateDoc } from 'firebase/firestore'
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { useAuth } from '@/auth/AuthProvider'
import { auth, db, storage } from '@/lib/firebase'
import { downscaleImage } from '@/lib/image'
import { Avatar, Button, Eyebrow, Field, Icons, Input, Notice, useToast } from '@/ui'
import { googlePhoto, useAccountIdentity } from './identity'
import { ImpersonationBanner } from './ImpersonationBanner'

/** The signed-in person's own account: how their name shows, and their photo. */
export function AccountPage() {
  const { user, impersonation } = useAuth()
  const { name, email, photo, uploaded, photoPath, roleLabel } = useAccountIdentity()
  const { toast } = useToast()
  const [draft, setDraft] = useState(user?.displayName ?? '')
  const [saving, setSaving] = useState(false)
  const trimmed = draft.trim()
  const changed = trimmed !== (user?.displayName ?? '')

  const save = async () => {
    const current = auth.currentUser
    if (!current || !trimmed) return
    setSaving(true)
    try {
      await updateProfile(current, { displayName: trimmed })
      await updateDoc(doc(db, `users/${current.uid}`), { displayName: trimmed })
      // Refresh the token so the header picks the new name up straight away.
      await current.getIdToken(true)
      toast('Name saved.', 'success')
    } catch {
      toast('Couldn’t save your name. Try again.', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <ImpersonationBanner />
      <main id="main" className="mx-auto grid w-full max-w-form gap-10 px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-small text-dim underline-offset-4 hover:text-ink hover:underline">
          <Icons.ArrowLeft size={12} /> Back to the portal
        </Link>
        <div className="grid gap-2">
          <Eyebrow tone="muted">Your account</Eyebrow>
          <h1 className="text-h2">Account settings</h1>
        </div>

        <section className="flex items-center gap-5">
          <Avatar name={name} src={photo} size="lg" />
          <div className="min-w-0">
            <p className="truncate text-h5 font-body text-ink">{name}</p>
            <p className="truncate text-small text-dim">{email}</p>
            <p className="mt-1 text-[0.6875rem] uppercase tracking-eyebrow text-dim">{roleLabel}</p>
          </div>
        </section>

        {impersonation ? (
          <Notice tone="info">You’re signed in as {name}. Their name and photo are theirs to change, so editing is off here.</Notice>
        ) : (
          <>
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault()
                void save()
              }}
            >
              <Field label="Your name" hint="How your name shows in the portal header and to the people you work with.">
                <Input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={80} autoComplete="name" />
              </Field>
              <div>
                <Button type="submit" disabled={!changed || !trimmed || saving} arrow={false}>
                  {saving ? 'Saving…' : 'Save name'}
                </Button>
              </div>
            </form>
            <PhotoSettings uploaded={uploaded} photoPath={photoPath} />
          </>
        )}
      </main>
    </>
  )
}

/**
 * Upload a photo that replaces the Google one everywhere the portal shows this person, replace it, or remove it to go
 * back to the Google photo (or initials). Stored at users/{uid}/avatar; the old file is deleted after a change.
 */
function PhotoSettings({ uploaded, photoPath }: { uploaded: string | null; photoPath: string | null }) {
  const { user } = useAuth()
  const { toast } = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null)
  const google = googlePhoto(user)
  const fallback = google ? 'your Google photo' : 'your initials'

  const dropOld = (path: string | null) => {
    if (path) deleteObject(ref(storage, path)).catch(() => undefined)
  }
  const upload = async (file: File) => {
    if (!user) return
    if (!file.type.startsWith('image/')) return toast('Choose an image file (JPG, PNG, HEIC…).', 'error')
    setBusy('upload')
    try {
      const blob = await downscaleImage(file, { maxWidth: 640, maxHeight: 640, quality: 0.88 })
      if (blob.size > 5 * 1024 * 1024) throw new Error('too large')
      const path = `users/${user.uid}/avatar/photo-${Date.now()}.jpg`
      const objectRef = ref(storage, path)
      await uploadBytes(objectRef, blob, { contentType: blob.type || file.type })
      await updateDoc(doc(db, `users/${user.uid}`), { photoURL: await getDownloadURL(objectRef), photoPath: path })
      dropOld(photoPath)
      toast('Photo updated.', 'success')
    } catch (e) {
      toast((e as Error).message === 'too large' ? 'That image is too large. Try one under 5 MB.' : 'Couldn’t upload your photo. Try again.', 'error')
    } finally {
      setBusy(null)
    }
  }
  const remove = async () => {
    if (!user) return
    setBusy('remove')
    try {
      await updateDoc(doc(db, `users/${user.uid}`), { photoURL: null, photoPath: null })
      dropOld(photoPath)
      toast(google ? 'Photo removed. Your Google photo is back.' : 'Photo removed.', 'success')
    } catch {
      toast('Couldn’t remove your photo. Try again.', 'error')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="grid gap-4 border-t border-gray-200 pt-8">
      <div className="grid gap-1">
        <h2 className="text-h5 font-body">Photo</h2>
        <p className="text-small text-charcoal">
          {uploaded
            ? `You’re using a photo you uploaded. Remove it to go back to ${fallback}.`
            : google
              ? 'This is the photo from the Google account you sign in with. Upload one to use a different photo here.'
              : 'Add a photo so the people you work with can put a face to your name.'}
        </p>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void upload(file)
        }}
      />
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <Button variant="ghost" arrow={false} disabled={Boolean(busy)} onClick={() => input.current?.click()}>
          {busy === 'upload' ? 'Uploading…' : uploaded ? 'Replace photo' : 'Upload a photo'}
        </Button>
        {uploaded && (
          <Button variant="link" arrow={false} disabled={Boolean(busy)} onClick={() => void remove()}>
            {busy === 'remove' ? 'Removing…' : google ? 'Use my Google photo' : 'Remove photo'}
          </Button>
        )}
      </div>
    </section>
  )
}
